import { Link } from 'react-router-dom';
import { projets as staticProjets } from '../data/siteData';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import { useEffect, useState } from 'react';
import { getPublicProjets, getPublicSettings } from '../services/contentApi';
import './Candidater.css';

const PROCESSUS = [
  { num: '01', title: 'Découvrez le programme', desc: "Lisez les détails du programme : objectifs, durée, domaines couverts et critères d'admission." },
  { num: '02', title: 'Déposez votre candidature', desc: "Remplissez le formulaire Google Form dédié au programme. Renseignez votre parcours et votre motivation." },
  { num: '03', title: 'Étude du dossier', desc: "Notre équipe étudie chaque candidature sous 48h. Vous recevrez une confirmation par email." },
  { num: '04', title: 'Entretien & sélection', desc: "Les candidats présélectionnés sont invités à un entretien de motivation avant la décision finale." },
];

const STATUS_LABEL = { en_cours: 'En cours', termine: 'Complété' };

const FAQ = [
  { q: 'Qui peut candidater aux programmes TTA ?', a: "Nos programmes sont ouverts à tout profil motivé — étudiant, jeune professionnel ou reconversion. Les critères spécifiques sont précisés sur chaque programme." },
  { q: 'Les programmes sont-ils payants ?', a: "Les conditions de participation sont précisées programme par programme, au moment de l'ouverture des candidatures. Plusieurs de nos cohortes ont été intégralement prises en charge grâce à des financements institutionnels." },
  { q: 'Quand démarre la Cohorte 2 ?', a: "Le calendrier n'est pas encore arrêté. En déposant votre candidature dès maintenant, vous serez informé en priorité de la date de démarrage et des modalités d'admission." },
  { q: 'Peut-on candidater à plusieurs programmes ?', a: "Oui, vous pouvez candidater à plusieurs programmes en cours. Remplissez un formulaire distinct pour chacun." },
];

export default function Candidater() {
  const { sections, page } = useCmsPage('candidater');
  useSeoMeta(page, {
    title: 'Candidater — Tech Talent Accelerator',
    desc: "Postulez aux programmes TTA financés par nos bailleurs institutionnels. Rejoignez la prochaine cohorte.",
  });
  const getSec = type => sections.find(s => s.type === type)?.content || {};
  const cmsHero = getSec('hero');

  const [projets, setProjets] = useState(staticProjets);
  const [openFaq, setOpenFaq] = useState(null);
  const [cmsLink, setCmsLink] = useState(null); // URL Google Form depuis Admin > Paramètres

  useEffect(() => {
    // Lien candidature depuis Admin > Paramètres (prioritaire sur siteData)
    getPublicSettings().then(s => {
      if (s?.forprode_candidature_link) setCmsLink(s.forprode_candidature_link);
    });
    // Données projets depuis API
    getPublicProjets().then(data => {
      if (!data || !data.length) return;
      setProjets(staticProjets.map(sp => {
        const live = data.find(a => a.id === sp.id);
        if (!live) return sp;
        return {
          ...sp,
          title:       live.title       || sp.title,
          description: live.description || sp.description,
          status:      STATUS_LABEL[live.status] || sp.status,
        };
      }));
    });
  }, []);

  const actifs  = projets.filter(p => p.isActive);
  const termines = projets.filter(p => !p.isActive);

  return (
    <main className="candidater-page">

      {/* ── HERO ── */}
      <section className="candidater-hero">
        <div className="candidater-hero__bg" />
        <div className="page-hero-right__orb" data-parallax="0.45" />
        <div className="page-hero-right__orb2" data-parallax="-0.20" />
        <div style={{ position:'relative', zIndex:2, textAlign:'center', maxWidth:'640px', margin:'0 auto' }}>
          <div className="section-eyebrow">{cmsHero.eyebrow || 'Programmes ouverts'}</div>
          <h1 className="section-title" style={{ fontSize:'42px' }}>
            {cmsHero.title || 'Candidater à un'}<br />
            <span className="grad-text">{cmsHero.title_grad || 'programme TTA'}</span>
          </h1>
          <p className="section-subtitle" style={{ maxWidth:'500px', margin:'0 auto' }}>
            {cmsHero.subtitle || "Bootcamps intensifs, certification reconnue et accompagnement vers l'emploi. Sélection sur dossier, entretien et test technique."}
          </p>
        </div>
      </section>

      {/* ── PROGRAMMES EN COURS ── */}
      <section className="section">
        <div className="section-eyebrow">Programmes en cours</div>
        <h2 className="section-title" style={{ fontSize:'32px' }}>Candidater maintenant</h2>
        {actifs.length === 0 ? (
          <div style={{ textAlign:'center', padding:'48px 0', color:'#888' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom:'16px' }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p style={{ fontSize:'15px', marginBottom:'8px', fontWeight:600 }}>Aucun programme ouvert pour le moment</p>
            <p style={{ fontSize:'13px', color:'#aaa' }}>Inscrivez-vous ci-dessous pour être notifié en priorité.</p>
          </div>
        ) : (
          <div className="candidater-programmes-grid">
            {actifs.map(p => (
              <div key={p.id} className="candidater-prog-card">
                <div className="candidater-prog-card__header">
                  <span className="candidater-prog-card__badge">
                    <span className="candidater-prog-card__badge-dot" />
                    {p.isUpcoming ? 'Candidatures ouvertes' : `En cours — ${p.year}`}
                  </span>
                  {p.partner && <span className="candidater-prog-card__partner">{p.partner}</span>}
                </div>
                <h3 className="candidater-prog-card__title">{p.title}</h3>
                <p className="candidater-prog-card__desc">{p.description}</p>
                <div className="candidater-prog-card__meta">
                  {p.duration && (
                    <span>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      {p.duration}
                    </span>
                  )}
                  {p.location && (
                    <span>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {p.location}
                    </span>
                  )}
                  {Number(p.participants) > 0 && (
                    <span>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      {p.participants} participants
                    </span>
                  )}
                </div>
                {p.formations && p.formations.length > 0 && (
                  <div className="candidater-prog-card__domaines">
                    {p.formations.map(f => <span key={f} className="candidater-prog-card__domaine">{f}</span>)}
                  </div>
                )}
                {(() => {
                  // Priorité : settings admin → sinon siteData
                  const link = (p.id === 'forprode-40-ia' && cmsLink) ? cmsLink : p.candidatureLink;
                  const hasLink = link && link !== '#';
                  return (
                <div className="candidater-prog-card__actions">
                  <a
                    href={hasLink ? link : undefined}
                    target={hasLink ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className={`btn-primary ${!hasLink ? 'btn-primary--disabled' : ''}`}
                    style={{ fontSize:'14px', padding:'12px 24px', display:'inline-flex', alignItems:'center', gap:'8px' }}
                    onClick={e => { if (!hasLink) e.preventDefault(); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    {!hasLink ? 'Ouverture prochaine' : 'Postuler via Google Form →'}
                  </a>
                  <Link to={`/projets/${p.slug}`} style={{ fontSize:'13px', color:'#888', textDecoration:'none', display:'flex', alignItems:'center', gap:'5px' }}>
                    En savoir plus →
                  </Link>
                </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── PROCESSUS ── */}
      <section className="section section-alt">
        <div className="section-eyebrow">Comment ça marche</div>
        <h2 className="section-title" style={{ fontSize:'32px' }}>Processus de candidature</h2>
        <div className="candidater-processus">
          {PROCESSUS.map((step, i) => (
            <div key={step.num} className="candidater-processus__step">
              <div className="candidater-processus__num">{step.num}</div>
              {i < PROCESSUS.length - 1 && <div className="candidater-processus__line" />}
              <div className="candidater-processus__content">
                <div className="candidater-processus__title">{step.title}</div>
                <div className="candidater-processus__desc">{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROGRAMMES À VENIR ── */}
      <section className="section">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'48px', alignItems:'center' }}>
          <div>
            <div className="section-eyebrow">Prochainement</div>
            <h2 className="section-title" style={{ fontSize:'30px' }}>Soyez notifié<br />en avant-première</h2>
            <p className="section-subtitle" style={{ fontSize:'14px' }}>
              De nouveaux programmes sont régulièrement lancés avec nos bailleurs partenaires. Laissez votre email pour être averti dès l'ouverture des candidatures.
            </p>
            {termines.length > 0 && (
              <div style={{ marginTop:'24px' }}>
                <div style={{ fontSize:'12px', color:'#aaa', marginBottom:'10px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Programmes passés</div>
                {termines.map(p => (
                  <Link key={p.id} to={`/projets/${p.slug}`} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 0', borderBottom:'1px solid #f0f0f0', textDecoration:'none' }}>
                    <span style={{ fontSize:'13px', fontWeight:600, color:'#333' }}>{p.title}</span>
                    <span style={{ fontSize:'11px', color:'#aaa', marginLeft:'auto' }}>{p.year} · Complété</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="candidater-notify-card">
            <h3 style={{ fontFamily:"'Sora',sans-serif", fontSize:'18px', fontWeight:700, marginBottom:'8px' }}>Restez informé</h3>
            <p style={{ fontSize:'13px', color:'#888', marginBottom:'20px', lineHeight:1.6 }}>Entrez votre email pour recevoir les annonces de nouveaux programmes en priorité.</p>
            <form onSubmit={e => { e.preventDefault(); alert('Merci ! Vous serez notifié à l\'ouverture du prochain programme.'); e.target.reset(); }} style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
              <input
                type="email" required placeholder="votre@email.com"
                style={{ flex:1, minWidth:'180px', padding:'11px 14px', borderRadius:'9px', border:'1px solid #e0e0e0', fontSize:'14px', fontFamily:"'Sora',sans-serif", outline:'none' }}
              />
              <button type="submit" className="btn-primary" style={{ fontSize:'13px', padding:'11px 20px', whiteSpace:'nowrap' }}>
                Me notifier →
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section section-alt">
        <div style={{ maxWidth:'680px', margin:'0 auto' }}>
          <div className="section-eyebrow" style={{ textAlign:'center' }}>FAQ</div>
          <h2 className="section-title" style={{ fontSize:'30px', textAlign:'center' }}>Questions fréquentes</h2>
          <div style={{ marginTop:'32px', display:'flex', flexDirection:'column', gap:'8px' }}>
            {FAQ.map((item, i) => (
              <div key={i} style={{ border:'1px solid #ebebeb', borderRadius:'10px', overflow:'hidden' }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', background:'#fff', border:'none', cursor:'pointer', fontFamily:"'Sora',sans-serif", fontSize:'14px', fontWeight:600, color:'#111', textAlign:'left', gap:'12px' }}
                >
                  {item.q}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5" style={{ flexShrink:0, transform: openFaq === i ? 'rotate(180deg)' : 'none', transition:'transform .2s' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {openFaq === i && (
                  <div style={{ padding:'0 20px 16px', fontSize:'13px', color:'#555', lineHeight:1.65 }}>{item.a}</div>
                )}
              </div>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:'32px' }}>
            <p style={{ fontSize:'14px', color:'#888', marginBottom:'12px' }}>Une question non listée ?</p>
            <Link to="/contact" className="btn-outline" style={{ fontSize:'13px' }}>Contacter l'équipe →</Link>
          </div>
        </div>
      </section>

    </main>
  );
}
